import { msg } from "@lingui/core/macro";
import { type Component, Show } from "solid-js";
import { ChevronDown, Download, FileIcon, Loader2, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@folio/shared";
import { formatBytes } from "@fleet/utils";

interface FileCardProps {
  name: string;
  size?: number | null;
  contentType?: string | null;
  uploadedAt?: string | null;
  isDownloading?: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
}

function formatUploadedDate(dateStr: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

export const FileCard: Component<FileCardProps> = (props) => {
  const { t, locale } = useI18n();

  return (
    <div
      class={cn(
        "group flex flex-col rounded-lg border border-border bg-card transition-all duration-300 min-w-0",
        props.isExpanded &&
          "border-primary/30 shadow-[0_0_12px_rgba(var(--accent-rgb),0.08)] bg-muted/10",
      )}
    >
      {/* Compact View Row (Semantic Button Trigger) */}
      <button
        type="button"
        aria-expanded={props.isExpanded}
        title={props.isExpanded ? t(msg`Click to close details`) : t(msg`Click to see details`)}
        onClick={() => {
          props.onToggleExpand();
        }}
        class={cn(
          "flex items-center gap-3 w-full min-w-0 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors hover:bg-muted/30 p-3 select-none",
          props.isExpanded ? "rounded-t-lg" : "rounded-lg",
        )}
      >
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-105">
          <FileIcon class="h-5 w-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{props.name}</p>
          <p class="text-[10px] text-muted-foreground truncate">
            {formatBytes(props.size ?? null)} •{" "}
            {props.contentType?.split("/")[1]?.toUpperCase() ?? "FILE"}
          </p>
        </div>
        <ChevronDown
          class={cn(
            "h-4 w-4 text-muted-foreground/60 transition-transform duration-300 shrink-0 mr-1",
            props.isExpanded && "rotate-180 text-primary",
          )}
        />
      </button>

      {/* Expanded Details Panel */}
      <div
        class={cn(
          "transition-all duration-300 ease-in-out overflow-hidden",
          props.isExpanded
            ? "max-h-[320px] opacity-100 px-3 pb-3 mt-3 pt-3 border-t border-border/40"
            : "max-h-0 opacity-0 pointer-events-none",
        )}
      >
        <div class="space-y-3 text-xs text-muted-foreground">
          {/* Full Name */}
          <div class="space-y-1">
            <span class="font-medium text-foreground/50">{t(msg`Full filename`)}</span>
            <p class="font-semibold text-foreground break-all bg-muted/20 p-2 rounded border border-border/40">
              {props.name}
            </p>
          </div>

          {/* Details Grid */}
          <div class="grid grid-cols-2 gap-2 bg-muted/10 p-2.5 rounded-lg border border-border/20">
            <div>
              <span class="text-[10px] uppercase font-semibold text-muted-foreground/60 block">
                {t(msg`Size`)}
              </span>
              <span class="font-medium text-foreground">{formatBytes(props.size ?? null)}</span>
            </div>
            <div>
              <span class="text-[10px] uppercase font-semibold text-muted-foreground/60 block">
                {t(msg`Type`)}
              </span>
              <span class="font-medium text-foreground truncate block">
                {props.contentType ?? t(msg`Unknown type`)}
              </span>
            </div>
            <div class="col-span-2">
              <span class="text-[10px] uppercase font-semibold text-muted-foreground/60 block">
                {t(msg`Uploaded on`)}
              </span>
              <span class="font-medium text-foreground">
                {props.uploadedAt ? formatUploadedDate(props.uploadedAt, locale()) : "–"}
              </span>
            </div>
          </div>

          {/* Actions Row */}
          <Show when={Boolean(props.onDownload) || Boolean(props.onDelete)}>
            <div class="flex gap-2 justify-end pt-1">
              <Show when={Boolean(props.onDownload)}>
                <Button
                  variant="outline"
                  size="sm"
                  class="h-8.5 text-xs flex items-center gap-1.5 shrink-0"
                  title={t(msg`Download attachment`)}
                  disabled={props.isDownloading}
                  onClick={() => {
                    props.onDownload?.();
                  }}
                >
                  <Show when={props.isDownloading} fallback={<Download class="h-3.5 w-3.5" />}>
                    <Loader2 class="h-3.5 w-3.5 animate-spin" />
                  </Show>
                  {t(msg`Download`)}
                </Button>
              </Show>
              <Show when={Boolean(props.onDelete)}>
                <Button
                  variant="ghost"
                  size="sm"
                  class="h-8.5 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive flex items-center gap-1.5 shrink-0"
                  title={t(msg`Delete attachment`)}
                  onClick={() => {
                    props.onDelete?.();
                  }}
                >
                  <Trash2 class="h-3.5 w-3.5" />
                  {t(msg`Delete`)}
                </Button>
              </Show>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};
